import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/useSEO';
import ConsultationForm from '@/components/common/ConsultationForm';
import PartnerDirectory from './components/PartnerDirectory';
import {
  ArrowRight, ArrowUpRight, Sparkles, Shield, CheckCircle2, Building2,
  TrendingUp, Clock, GraduationCap, Globe2, BadgeCheck, Quote, MapPin,
  Filter, Compass, Handshake, Award, Layers, Eye,
} from 'lucide-react';

/**
 * GlobalPartnershipsPage — `/study-abroad/global-partnerships/`
 * ────────────────────────────────────────────────────────────────
 * Institution-grade trust page that positions Zafco as the bridge
 * between its New Delhi HQ and 500+ vetted global universities.
 *
 * Layout (per user spec):
 *   1. Credibility Hero (New Delhi HQ + Global Mobility Expert)
 *   2. The Zafco Filter (Vetting Strategy Box — 3 strategic questions)
 *   3. Network Statistics Bar (500+ / 15+ / 100% Vetted)
 *   4. Depth Across Major Destinations (5-col flag icon grid)
 *   5. Benefit Cards (Faster Decisions, Targeted Scholarships)
 *   6. Ethics Badge / Trust Box
 *   7. Inline Lead Capture
 *   8. Final CTA
 */

const DESTINATIONS = [
  { flag: '🇬🇧', name: 'UK',        unis: '120+ partners', href: '/study-abroad/uk/' },
  { flag: '🇨🇦', name: 'Canada',    unis: '95+ partners',  href: '/study-abroad/canada/' },
  { flag: '🇦🇺', name: 'Australia', unis: '80+ partners',  href: '/study-abroad/australia/' },
  { flag: '🇮🇪', name: 'Ireland',   unis: '35+ partners',  href: '/study-abroad/ireland/' },
  { flag: '🇦🇪', name: 'Dubai/UAE', unis: '40+ partners',  href: '/study-abroad/dubai/' },
];

const FILTER_QUESTIONS = [
  {
    Icon: TrendingUp,
    q: 'Do graduates actually get hired?',
    a: 'We benchmark every partner on real graduate-employment data — first-job rates, average starting salary, sector concentration — not glossy brochures.',
  },
  {
    Icon: Shield,
    q: 'Does the institution align with the visa system?',
    a: 'Course + provider must be on the right CRICOS / DLI / HEAR / Tier-4 sponsor / Knowledge Park list. We refuse partners that risk visa refusal or PR ineligibility.',
  },
  {
    Icon: Compass,
    q: 'Does it reduce friction for the student?',
    a: 'Conditional offers, fee-waiver windows, application-fee waivers, faster decisions — partners are filtered by how much friction they remove for Zafco students.',
  },
];

const STATS = [
  { Icon: Building2, n: '500+',  label: 'Vetted Universities' },
  { Icon: Globe2,    n: '15+',   label: 'Countries' },
  { Icon: BadgeCheck,n: '100%',  label: '2026 Compliance' },
];

const BENEFITS = [
  {
    Icon: Clock,
    title: 'Faster Decisions',
    pillar: 'Average 7–14 day turnaround',
    body: 'Zafco students sit in priority queues at partner universities. Instead of the standard 4–6 week wait, conditional offers typically come back in 7–14 days — critical when you are chasing a January or September intake.',
    points: ['Priority application channel', 'Direct admissions desk', 'Pre-screened document pack'],
  },
  {
    Icon: GraduationCap,
    title: 'Targeted Scholarships',
    pillar: '₹2L – ₹15L+ partner scholarships',
    body: 'Many of our partnerships include exclusive scholarship windows that are not advertised publicly — early-bird tuition waivers, merit awards, India-specific bursaries, and country-of-origin scholarships ranging from ₹2 lakh to ₹15 lakh+.',
    points: ['Early-bird tuition waivers', 'India / South-Asia bursaries', 'Merit-based partial scholarships'],
  },
];

const ETHICS = [
  'We refuse partnerships that survive on commission alone — the institution must earn its place by graduate outcomes and visa-system alignment.',
  'We do not steer students to a partner just because a higher referral fee exists. Country-neutral, career-first.',
  'We disclose, in writing, whether a recommendation is a partner institution or an independently chosen fit — your decision is informed, not engineered.',
  'We continuously re-vet — partners are removed from our active list if outcomes drop, visa refusal rates rise, or post-study work pathways are revoked.',
];

const SectionHeading = ({ eyebrow, title, subtitle, light }) => (
  <div className={`max-w-3xl mb-8 lg:mb-12 ${light ? 'text-white' : ''}`}>
    {eyebrow && (
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3 ${
        light
          ? 'bg-[#C5A028]/15 border border-[#C5A028]/35 text-[#FACC15]'
          : 'bg-[#C5A028]/12 border border-[#C5A028]/35 text-[#C5A028]'
      } text-[10px] font-bold tracking-[0.22em] uppercase`}>
        {eyebrow}
      </div>
    )}
    <h2 className={`text-2xl sm:text-3xl lg:text-[36px] font-bold font-syne leading-tight mb-3 ${
      light ? 'text-white' : 'text-[#003087]'
    }`}>
      {title}
    </h2>
    {subtitle && (
      <p className={`leading-relaxed text-sm sm:text-base ${light ? 'text-slate-300' : 'text-slate-600'}`}>
        {subtitle}
      </p>
    )}
  </div>
);

const GlobalPartnershipsPage = () => {
  usePageMeta(
    'Global University Partnerships | 500+ Vetted Institutions | Zafco',
    'Explore Zafco\u2019s curated network of 500+ universities across the UK, Canada, Australia, Ireland, and Dubai. Career-aligned partnerships focused on 2026 labor market outcomes.',
    { canonical: '/study-abroad/global-partnerships/' },
  );

  // JSON-LD: WebPage (references sitewide #organization) + BreadcrumbList
  useEffect(() => {
    const webpage = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': 'https://zafco.in/study-abroad/global-partnerships/#webpage',
      url: 'https://zafco.in/study-abroad/global-partnerships/',
      name: 'Global University Partnerships',
      isPartOf: { '@id': 'https://zafco.in/#website' },
      about: { '@id': 'https://zafco.in/#organization' },
      description:
        'Curated network of 500+ universities across the UK, Canada, Australia, Ireland, and Dubai, vetted for 2026 labor market outcomes.',
      inLanguage: 'en-IN',
    };
    const bc = {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',         item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Study Abroad', item: 'https://zafco.in/study-abroad/' },
        { '@type': 'ListItem', position: 3, name: 'Global Partnerships' },
      ],
    };
    const a = document.createElement('script');
    a.type = 'application/ld+json'; a.setAttribute('data-gp-org', '1'); a.text = JSON.stringify(webpage);
    const b = document.createElement('script');
    b.type = 'application/ld+json'; b.setAttribute('data-gp-bc',  '1'); b.text = JSON.stringify(bc);
    document.head.appendChild(a); document.head.appendChild(b);
    return () => {
      document.head.querySelector('script[data-gp-org]')?.remove();
      document.head.querySelector('script[data-gp-bc]')?.remove();
    };
  }, []);

  return (
    <div className="bg-white zaf-prose" data-testid="gp-page">
      {/* ============ HERO ============ */}
      <section
        className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 55%, #020617 100%)' }}
      >
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#C5A028]/14 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">
                  <Sparkles className="w-3.5 h-3.5" /> Global Mobility Expert
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-[10.5px] font-bold tracking-[0.22em] text-emerald-200 uppercase">
                  <MapPin className="w-3.5 h-3.5" /> HQ · New Delhi, India
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-[52px] font-bold font-syne leading-[1.05] tracking-tight mb-5" data-testid="gp-h1">
                Global University Partnerships:{' '}
                <span className="text-[#FACC15]">Strategic, Not Just Symbolic.</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-200 leading-snug max-w-2xl mb-3 font-semibold">
                A curated bridge between Zafco&apos;s New Delhi HQ and 500+ vetted global institutions.
              </p>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed max-w-2xl mb-7">
                Most agencies list logos. Zafco lists <strong className="text-white">accountable institutions</strong> — partners that have been filtered for graduate outcomes, visa-system alignment, and friction reduction. Every name in our network is on the list because it earns its place each year.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/study-abroad/free-profile-evaluation/?source=global-partnerships-hero"
                  className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm sm:text-base px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C5A028]/30"
                  data-testid="gp-hero-cta"
                >
                  Check Your Eligibility for Partner Scholarships <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="#partner-form"
                  className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm sm:text-base px-7 py-3.5 rounded-full transition-all"
                  data-testid="gp-hero-secondary"
                >
                  Talk to a Partnerships Counsellor
                </a>
              </div>
            </div>

            {/* Hero — flag cluster card */}
            <div className="rounded-3xl border border-white/15 bg-white/5 backdrop-blur-sm p-6 sm:p-7" data-testid="gp-hero-flags">
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-3">Active Partnership Lanes</div>
              <div className="grid grid-cols-5 gap-2.5">
                {DESTINATIONS.map(d => (
                  <Link
                    key={d.name}
                    to={d.href}
                    className="group flex flex-col items-center gap-1.5 px-1.5 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-[#C5A028]/50 hover:bg-white/10 transition-all"
                    data-testid={`gp-hero-flag-${d.name.toLowerCase().replace(/\//g, '-')}`}
                  >
                    <span className="text-2xl">{d.flag}</span>
                    <span className="text-[10px] font-bold font-syne text-white group-hover:text-[#FACC15] transition-colors text-center leading-tight">{d.name}</span>
                  </Link>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-white/10 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-2xl font-bold font-syne text-[#FACC15]">500+</div>
                  <div className="text-[9px] font-bold tracking-[0.18em] text-slate-400 uppercase">Universities</div>
                </div>
                <div>
                  <div className="text-2xl font-bold font-syne text-[#FACC15]">15+</div>
                  <div className="text-[9px] font-bold tracking-[0.18em] text-slate-400 uppercase">Countries</div>
                </div>
                <div>
                  <div className="text-2xl font-bold font-syne text-[#FACC15]">100%</div>
                  <div className="text-[9px] font-bold tracking-[0.18em] text-slate-400 uppercase">Vetted 2026</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ NETWORK STATISTICS BAR ============ */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="gp-stats-bar">
        <div className="max-w-7xl mx-auto px-6 py-8 lg:py-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-8">
            {STATS.map(s => (
              <div key={s.label} className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#003087]/8 border border-[#003087]/20 flex items-center justify-center shrink-0">
                  <s.Icon className="w-7 h-7 text-[#003087]" aria-hidden="true" />
                </div>
                <div>
                  <div className="text-3xl sm:text-[34px] font-bold font-syne text-[#003087] leading-none">{s.n}</div>
                  <div className="text-[11px] font-bold tracking-[0.18em] text-slate-500 uppercase mt-1">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE ZAFCO FILTER (Vetting Strategy Box) ============ */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="The Vetting Strategy"
            title="The Zafco Filter."
            subtitle="Before a university enters our active partner list, it must satisfy three strategic questions. Most schools that approach us do not pass. That filter is exactly what protects every Zafco student."
          />
          <div
            className="rounded-3xl border-2 border-[#C5A028]/40 bg-gradient-to-br from-amber-50/40 via-white to-amber-50/40 p-6 sm:p-9 shadow-lg relative overflow-hidden"
            data-testid="gp-filter-box"
          >
            <div aria-hidden="true" className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#C5A028]/12 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-7 pb-5 border-b border-amber-200/60">
                <div className="w-12 h-12 rounded-xl bg-[#C5A028]/15 border border-[#C5A028]/40 flex items-center justify-center">
                  <Filter className="w-6 h-6 text-[#C5A028]" aria-hidden="true" />
                </div>
                <div>
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase">Strategic, not symbolic</div>
                  <div className="text-lg sm:text-xl font-bold font-syne text-[#003087]">Three questions every partner must answer.</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-5">
                {FILTER_QUESTIONS.map((f, idx) => (
                  <article
                    key={f.q}
                    className="bg-white/80 backdrop-blur-sm rounded-2xl border border-amber-200/60 p-5"
                    data-testid={`gp-filter-${idx}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#003087]/8 border border-[#003087]/20 flex items-center justify-center shrink-0">
                        <f.Icon className="w-5 h-5 text-[#003087]" aria-hidden="true" />
                      </div>
                      <div className="text-[10px] font-bold tracking-[0.2em] text-[#C5A028] uppercase">Question {idx + 1}</div>
                    </div>
                    <h3 className="text-base font-bold font-syne text-[#003087] mb-2 leading-tight">{f.q}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{f.a}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PARTNER DIRECTORY (Searchable + Filterable) ============ */}
      <PartnerDirectory />

      {/* ============ DEPTH ACROSS MAJOR DESTINATIONS ============ */}
      <section className="bg-slate-50 py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Depth Across Major Destinations"
            title="500+ Universities. Five Career-Migration Lanes."
            subtitle={
              <>The partnerships are concentrated where the work-rights and PR systems are most favourable to Indian students in 2026 —{' '}
                <Link to="/study-abroad/uk/" className="text-[#003087] font-semibold underline decoration-[#C5A028] underline-offset-4 hover:text-[#C5A028]">UK</Link>,{' '}
                <Link to="/study-abroad/canada/" className="text-[#003087] font-semibold underline decoration-[#C5A028] underline-offset-4 hover:text-[#C5A028]">Canada</Link>,{' '}
                <Link to="/study-abroad/australia/" className="text-[#003087] font-semibold underline decoration-[#C5A028] underline-offset-4 hover:text-[#C5A028]">Australia</Link>,{' '}
                <Link to="/study-abroad/ireland/" className="text-[#003087] font-semibold underline decoration-[#C5A028] underline-offset-4 hover:text-[#C5A028]">Ireland</Link>, and{' '}
                <Link to="/study-abroad/dubai/" className="text-[#003087] font-semibold underline decoration-[#C5A028] underline-offset-4 hover:text-[#C5A028]">Dubai/UAE</Link>.
              </>
            }
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="gp-destination-grid">
            {DESTINATIONS.map(d => (
              <Link
                key={d.name}
                to={d.href}
                className="group flex flex-col items-center gap-2.5 p-5 rounded-2xl bg-white border border-slate-200 hover:border-[#C5A028] hover:shadow-md hover:-translate-y-1 transition-all"
                data-testid={`gp-dest-${d.name.toLowerCase().replace(/\//g, '-')}`}
              >
                <span className="text-4xl">{d.flag}</span>
                <span className="text-base font-bold font-syne text-[#003087] group-hover:text-[#C5A028] transition-colors">{d.name}</span>
                <span className="text-[11px] font-semibold text-slate-500 group-hover:text-[#C5A028] transition-colors">{d.unis}</span>
                <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-slate-400 group-hover:text-[#C5A028] transition-colors inline-flex items-center gap-1">
                  Explore <ArrowUpRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-500 leading-relaxed max-w-2xl mx-auto">
              Partnerships extend further into Germany, New Zealand, France, Netherlands, and Singapore — surfaced contextually based on profile fit during your counselling session.
            </p>
          </div>
        </div>
      </section>

      {/* ============ BENEFITS — Side-by-side feature cards ============ */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Why It Matters For You"
            title="Faster Decisions. Targeted Scholarships."
            subtitle="The day-to-day proof of a partnership is not the logo wall — it is the speed of your offer letter and the scholarships unlocked for your file."
          />
          <div className="grid lg:grid-cols-2 gap-5" data-testid="gp-benefits-grid">
            {BENEFITS.map((b, idx) => (
              <article
                key={b.title}
                className={`rounded-3xl p-6 sm:p-8 border-2 transition-all hover:-translate-y-1 relative overflow-hidden ${
                  idx === 0
                    ? 'bg-gradient-to-br from-blue-50 to-white border-[#003087]/20 hover:border-[#003087]/40'
                    : 'bg-gradient-to-br from-amber-50/60 to-white border-[#C5A028]/30 hover:border-[#C5A028]/60'
                }`}
                data-testid={`gp-benefit-${idx}`}
              >
                <div className={`absolute -top-10 -right-10 w-44 h-44 rounded-full blur-3xl ${
                  idx === 0 ? 'bg-[#003087]/10' : 'bg-[#C5A028]/15'
                }`} aria-hidden="true" />
                <div className="relative">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${
                    idx === 0
                      ? 'bg-[#003087]/10 border border-[#003087]/25'
                      : 'bg-[#C5A028]/15 border border-[#C5A028]/40'
                  }`}>
                    <b.Icon className={`w-7 h-7 ${idx === 0 ? 'text-[#003087]' : 'text-[#C5A028]'}`} aria-hidden="true" />
                  </div>
                  <div className={`text-[10px] font-bold tracking-[0.22em] uppercase mb-2 ${
                    idx === 0 ? 'text-[#003087]' : 'text-[#C5A028]'
                  }`}>{b.pillar}</div>
                  <h3 className="text-xl sm:text-2xl font-bold font-syne text-[#003087] mb-3 leading-tight">{b.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-5">{b.body}</p>
                  <ul className="space-y-2">
                    {b.points.map(p => (
                      <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${idx === 0 ? 'text-[#003087]' : 'text-[#C5A028]'}`} aria-hidden="true" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE ETHICS BADGE (Trust Box) ============ */}
      <section className="bg-slate-50 py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading
            eyebrow="Trust Layer"
            title="Transparent, Ethics-Led Partner Selection."
            subtitle="Listing a partner is a regulated decision at Zafco, not a marketing choice. These are the four lines we will not cross — even when crossing them would close more files."
          />
          <div
            className="rounded-3xl bg-white border-2 border-emerald-300/50 shadow-md p-6 sm:p-9 relative overflow-hidden"
            data-testid="gp-ethics-box"
          >
            <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-100/50 blur-3xl" />
            <div className="relative grid sm:grid-cols-[auto_1fr] gap-6 sm:gap-8 items-start">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-emerald-100 border border-emerald-300 flex items-center justify-center mx-auto sm:mx-0 shrink-0">
                <Eye className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-700" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-bold tracking-[0.22em] text-emerald-700 uppercase mb-1">Ethics Badge</div>
                <h3 className="text-lg sm:text-xl font-bold font-syne text-[#003087] mb-4">Four lines we will not cross.</h3>
                <ul className="space-y-3" data-testid="gp-ethics-list">
                  {ETHICS.map((e, idx) => (
                    <li key={e} className="flex items-start gap-3 text-sm sm:text-[15px] text-slate-700 leading-relaxed" data-testid={`gp-ethic-${idx}`}>
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" aria-hidden="true" />
                      <span>{e}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ INLINE LEAD FORM ============ */}
      <section
        className="relative overflow-hidden text-white py-14 lg:py-20"
        style={{ background: 'linear-gradient(180deg, #020617 0%, #001F5C 50%, #003087 100%)' }}
        data-testid="gp-form-section"
        id="partner-form"
      >
        <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -translate-y-1/2 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#C5A028]/12 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-[#003087]/40 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/40 text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-4">
                <Handshake className="w-3.5 h-3.5" /> Partner Scholarships
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-[40px] font-bold font-syne text-white leading-[1.1] mb-4">
                Unlock <span className="text-[#FACC15]">partner scholarships</span> & priority offer windows.
              </h2>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-6">
                Share your profile in 30 seconds and a partnerships counsellor will map which of our 500+ partner institutions you qualify for — including unadvertised scholarships, fee-waiver intakes, and priority decision queues.
              </p>
              <ul className="space-y-3" data-testid="gp-form-bullets">
                <li className="flex items-start gap-3 text-sm sm:text-[15px] text-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-[#FACC15] mt-0.5 shrink-0" aria-hidden="true" />
                  <span><strong className="text-white">Partner-only scholarships</strong> — typically ₹2L – ₹15L+, not visible on public university websites.</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-[15px] text-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-[#FACC15] mt-0.5 shrink-0" aria-hidden="true" />
                  <span><strong className="text-white">7–14 day offer turnaround</strong> via Zafco&apos;s priority admissions channel.</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-[15px] text-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-[#FACC15] mt-0.5 shrink-0" aria-hidden="true" />
                  <span><strong className="text-white">Career + visa alignment</strong> — only partners on the right sponsor lists for your target work-rights pathway.</span>
                </li>
              </ul>
            </div>

            <div
              className="rounded-3xl bg-white/8 backdrop-blur-md border border-white/15 p-5 sm:p-7 shadow-2xl shadow-[#020617]/50 relative"
              data-testid="gp-form-card"
            >
              <div className="absolute -top-3 left-6 inline-flex items-center gap-2 bg-[#C5A028] text-black text-[10px] font-bold tracking-[0.22em] uppercase px-3 py-1 rounded-full shadow-md">
                <Sparkles className="w-3 h-3" /> Scholarship Match
              </div>
              <div className="mb-4 pt-1">
                <h3 className="text-xl sm:text-2xl font-bold font-syne text-white leading-tight">Match me to partner universities.</h3>
                <p className="text-xs sm:text-sm text-slate-400 mt-1">Counsellor will reply within 60 seconds via WhatsApp.</p>
              </div>
              <ConsultationForm variant="dark" source="study-abroad-global-partnerships-form" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-white py-14 lg:py-20" data-testid="gp-final-cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#C5A028]/12 border border-[#C5A028]/35 mb-5">
            <Award className="w-8 h-8 text-[#C5A028]" aria-hidden="true" />
          </div>
          <h2 className="text-2xl md:text-4xl font-bold font-syne mb-4 leading-tight text-[#003087]">
            A partner network that actually <span className="text-[#C5A028]">works for you.</span>
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto mb-7 leading-relaxed text-sm sm:text-base">
            Stop comparing logos. Start comparing graduate outcomes, visa-system alignment, and scholarship windows. Our counsellors will tell you — honestly — which of our 500+ partners is the right fit for your profile, your budget, and your career.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/study-abroad/free-profile-evaluation/?source=global-partnerships-final-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C5A028]/30"
              data-testid="gp-final-cta"
            >
              Check Your Eligibility for Partner Scholarships <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/study-abroad/why-choose-zafco/"
              className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-[#003087] border border-slate-200 font-semibold px-7 py-3.5 rounded-full transition-colors"
              data-testid="gp-final-secondary"
            >
              Why Choose Zafco
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GlobalPartnershipsPage;
